import { useState, useRef, useMemo } from 'preact/hooks';
import emitter from '@webcore/core/events/eventemitter';
import { useXRErrorGuidance } from '@core/hooks/useXRErrorGuidance';
import useUserDevice from '@core/hooks/useUserDevice';
import { Spinner } from '@core/components/Elements';
import { Carousel, CarouselRef } from '@core/components/Elements/Carousel';
import { Slide } from '@core/components/Elements/Carousel/Slide';
import Button from '@core/components/Elements/Button/Button';

// Shared assets
import Background from '@assets-shared/images/background.webp';
import Lockup from '@assets-shared/images/lockup.webp';

// ============================================================================
// TEMPLATE CONFIGURATION - Customize these for your project
// ============================================================================
const CONFIG = {
  // Onboarding slides content
  slides: [
    {
      title: 'WebXR Experience',
      description: 'This uses WebXR for immersive AR directly in your browser. You can record and share your experience!',
    },
    {
      title: 'Permissions Required',
      description: 'Access to your camera, motion & orientation is required for the AR experience.',
    },
  ],
  
  // Button text
  nextButtonText: 'NEXT',
  beginButtonText: 'BEGIN',
};

interface SplashProps {
  isLoading: boolean;
  onClose: () => void;
  onBack?: () => void;
  contentTitle?: string;
  /** When true, skips the carousel/begin button and shows "Starting AR..." after loading */
  autoStart?: boolean;
}

export function Splash({ isLoading, onClose, onBack, contentTitle, autoStart }: SplashProps) {
  const { device } = useUserDevice();
  const { errorMessage, permissionStatus } = useXRErrorGuidance();
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const deviceType = useMemo(() => {
    if (device === undefined) return 'loading';
    if (device === 'iOS' || device === 'Android' || device === 'AppClip') return 'mobile';
    return 'desktop';
  }, [device]);

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollNext();
    }
  };

  const handleBegin = () => {
    // onClose triggers XR start via App.tsx, no need to also emit xr-start
    onClose();
  };

  const isLastSlide = currentSlide === CONFIG.slides.length - 1;

  return (
    <div 
      className="fixed inset-0 flex flex-col text-black overflow-hidden pointer-events-auto z-50"
      style={{
        backgroundImage: `url(${Background})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        minHeight: '100dvh',
      }}
    >
      <div className="flex-1 min-h-0 flex flex-col z-20 px-5 py-5 justify-between">
        
        {/* Back button - shown when onBack is provided */}
        {onBack && !isLoading && deviceType !== 'loading' && (
          <button
            onClick={onBack}
            className="absolute top-6 left-5 z-30 flex items-center gap-2 text-black/60 hover:text-black transition-colors"
            aria-label="Go back"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        )}
        
        {/* Loading state or Auto-start waiting state */}
        {deviceType === 'loading' || isLoading || autoStart ? (
          <>
            {/* Header with Logo */}
            <header className="flex flex-col justify-center items-center pt-8 z-20 px-5 pb-8 h-[200px] flex-1">
              <img
                src={Lockup}
                alt="Experience Title"
                className="w-full max-w-[300px]"
              />
            </header>
            
            {/* Spinner */}
            <div className="flex flex-col items-center justify-center gap-6 py-16">
              <Spinner size="2md" variant="primary" />
              {autoStart && !isLoading && deviceType !== 'loading' && (
                <p className="text-black/60 text-sm">Starting AR...</p>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Header with Logo */}
            <header className="flex flex-col items-center pt-8 z-20 px-5 pb-8">
              <img
                src={Lockup}
                alt="Experience Title"
                className="w-full max-w-[300px]"
              />
              {contentTitle && (
                <p className="text-black/60 text-sm mt-4 font-medium uppercase tracking-wider">
                  {contentTitle}
                </p>
              )}
            </header>

            {/* Error message */}
            {errorMessage ? (
              <div className="flex items-center justify-center h-full">
                <div
                  className="text-2xl short:text-xl text-center font-light animate-fade-in pb-8 short:pb-4"
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              </div>
            ) : (
              <>
                {/* Desktop fallback */}
                {deviceType === 'desktop' ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-[22px] mb-4">This experience is designed for mobile devices</p>
                      <p className="text-black/60">Scan the QR code or open this URL on your phone</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Onboarding Carousel */}
                    <div className="flex-1 min-h-0 h-full">
                      <Carousel
                        ref={carouselRef}
                        dotColor="black"
                        onSelectedChange={setCurrentSlide}
                      >
                        {CONFIG.slides.map((slide, index) => (
                          <Slide key={index}>
                            <div className="h-full flex flex-col pointer-events-none">
                              <h2 className="text-[22px] leading-tight uppercase font-bold mb-6">
                                {slide.title}
                              </h2>
                              <p className="text-[16px] font-light">
                                {slide.description}
                              </p>
                            </div>
                          </Slide>
                        ))}
                      </Carousel>
                    </div>

                    {/* Navigation button */}
                    {permissionStatus !== 'denied-motion' && (
                      <div className="pb-8 pt-4 flex-shrink-0">
                        <Button 
                          onClick={isLastSlide ? handleBegin : handleNext} 
                          className="animate-fade-in"
                        >
                          {isLastSlide ? CONFIG.beginButtonText : CONFIG.nextButtonText}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

